import React, { createContext, useState, useContext, useEffect } from "react";
import { TEMARIO } from "../constants/temario";
import { useAuth } from "../contexts/AuthContext";
import PremiumModal from "../components/Billing/PremiumModal";
import { siteConfig } from "../config/siteConfig";

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp debe usarse dentro de AppProvider");
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [view, setView] = useState("dashboard");
  const [currentExam, setCurrentExam] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);

  const { profile, isLoadingProfile, updateProfile } = useAuth();
  const isPremium = profile?.is_premium === true;

  const navigateToProtectedFeature = (targetView, examData = null) => {
    const protectedViews = ["exam", "practice"];
    if (protectedViews.includes(targetView) && !isPremium) {
      setIsPremiumModalOpen(true);
      return;
    }
    if (examData) setCurrentExam(examData);
    setView(targetView);
  };

  const [aiChatMessages, setAiChatMessages] = useState([
    {
      id: "1",
      text: siteConfig.ai.welcomeMessage,
      isUser: false,
      timestamp: new Date(),
    },
  ]);

  const calculateOverallProgress = () => {
    const progressData = profile?.progress || {};
    if (!TEMARIO || TEMARIO.length === 0) return 0;
    const total = TEMARIO.reduce(
      (acc, topic) => acc + (progressData[topic.id] || 0),
      0
    );
    return Math.round(total / TEMARIO.length);
  };

  const markTopicAsRead = async (topicId) => {
    if (!profile) return;
    const currentRead = Array.isArray(profile.read_laws) ? profile.read_laws : [];
    let updatedLaws;

    if (currentRead.includes(topicId)) {
      updatedLaws = currentRead.filter((id) => id !== topicId);
    } else {
      updatedLaws = [...currentRead, topicId];
    }

    try {
      await updateProfile({ read_laws: updatedLaws });
    } catch (error) {
      console.error("Error al actualizar estado de lectura:", error);
    }
  };

  const updateTopicProgress = async (topicId, score) => {
    if (!profile || !isPremium) return;
    const currentProgress = profile?.progress || {};
    const oldScore = currentProgress[topicId] || 0;

    if (score > oldScore) {
      const updatedProgress = { ...currentProgress, [topicId]: score };
      await updateProfile({ progress: updatedProgress });
    }
  };

  const addExamResult = async (score, totalQuestions) => {
    if (!profile || !isPremium) return;
    const percentage = Math.round((score / totalQuestions) * 100);
    const newExamsCount = (profile?.exam_count || 0) + 1;
    const newBestScore = Math.max(percentage, profile?.best_score || 0);

    await updateProfile({
      exam_count: newExamsCount,
      best_score: newBestScore,
    });
  };

  const addAIMessage = (text, isUser = false, customId = null) => {
    const newMessage = {
      id: customId || Date.now().toString(),
      text,
      isUser,
      timestamp: new Date(),
    };
    setAiChatMessages((prev) => [...prev, newMessage]);
  };

  useEffect(() => {
    if (view !== "exam") {
      setCurrentExam(null);
    }
  }, [view]);

  useEffect(() => {
    if (currentExam?.questions) {
      const questionsWithAnswered = currentExam.questions.map((q) => ({
        ...q,
        answered: q.answered || false,
        selectedAnswer: q.selectedAnswer || null,
      }));

      if (
        JSON.stringify(questionsWithAnswered) !==
        JSON.stringify(currentExam.questions)
      ) {
        setCurrentExam((prev) => ({
          ...prev,
          questions: questionsWithAnswered,
        }));
      }
    }
  }, [currentExam?.sourceId]);

  return (
    <AppContext.Provider
      value={{
        view,
        setView,
        navigateToProtectedFeature,
        profile,
        isPremium,
        isLoadingProfile,
        currentExam,
        setCurrentExam,
        isChatOpen,
        setIsChatOpen,
        aiChatMessages,
        setAiChatMessages,
        calculateOverallProgress,
        markTopicAsRead,
        updateTopicProgress,
        addExamResult,
        addAIMessage,
      }}
    >
      {children}

      <PremiumModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
        onGoToBilling={() => {
          setIsPremiumModalOpen(false);
          setView("billing");
        }}
      />
    </AppContext.Provider>
  );
};
